import type { NextPage } from 'next'
import Link from 'next/link'
import BodyText from '../lib/components/body-text'
import PageHeader from '../lib/components/page-header'
import { Accordion, AccordionItem as Item } from '@szhsin/react-accordion'
import Screenshot from '../lib/components/Screenshot'

/**
 * @type {React.ExoticComponent<import('@szhsin/react-accordion').AccordionItemProps>}
 */
const AccordionItem = ({
  header,
  ...rest
}: {
  header: string
  initialEntered?: boolean
  children: React.ReactNode
}) => (
  <Item
    {...rest}
    header={({ state: { isEnter } }) => (
      <>
        <img
          className={`ml-auto mr-2 transition-transform duration-200 ease-out ${isEnter && 'rotate-180'}`}
          src="./chevron.svg"
          alt="Chevron"
        />
        {header}
      </>
    )}
    buttonProps={{
      className: ({ isEnter }) => `flex pt-4 mt-4 text-left ${isEnter && ''}`,
    }}
    contentProps={{
      className: 'transition-height duration-200 ease-out',
    }}
    panelProps={{ className: 'p-1' }}
  />
)

const Login: NextPage = () => {
  return (
    <>
      <PageHeader title="Frequently asked questions" />
      <div className="mt-4">
        <Accordion transition transitionTimeout={200} allowMultiple>
          <AccordionItem header="What's the difference between a prototype and a component?" initialEntered>
            <BodyText>
              Use <strong className="text-sm font-semibold text-slate-700">prototypes</strong> to generate full app or
              website screens. For example, supplying <em>"saas collaboration app home screen on a mobile device"</em>{' '}
              will generate something like:
            </BodyText>
            <div className="mb-4 mt-2">
              <Screenshot src="prototype-example.png" />
            </div>
            <BodyText>
              Alternatively, <strong className="text-sm font-semibold text-slate-700">components</strong> will generate
              native Figma components for you to reuse throughout your design. Use these to create smaller functional
              components, such as a <em>"customer testimony card":</em>
            </BodyText>
            <div className="mb-4 mt-2">
              <Screenshot src="component-example.png" />
            </div>
          </AccordionItem>
          <AccordionItem header="How do refinements work?">
            <div className="mb-4 mt-2">
              <Screenshot src="refinement-example.gif" />
            </div>
            <BodyText>
              Refinements are additional input you can provide for what the AI just generated. sprouts.design will
              remember what you said originally, so you can use natural language.
            </BodyText>
            <BodyText>Some things to keep in mind:</BodyText>
            <ul className="my-2 ml-6 list-disc text-sm font-light">
              <li>
                When you submit a refinement, the{' '}
                <strong className="text-sm font-semibold text-slate-700">old design will disappear</strong>
              </li>
              <li>Once you create a new design, you cannot refine an earlier one</li>
              <li>
                sprouts.design also understands Figma concepts so you can reference specific component names it
                generated.
              </li>
            </ul>
          </AccordionItem>
          <AccordionItem header="How do I improve the prototypes I'm getting?">
            <BodyText>Prototypes work best when your prompts include:</BodyText>
            <ul className="my-2 ml-6 list-disc text-sm font-light">
              <li>
                The name of a page, such as <em>"the settings page"</em>
              </li>
              <li>
                A screen type like <em>"mobile app"</em> or <em>"website"</em>
              </li>
            </ul>
            <BodyText>
              Try to avoid vague general ideas or assuming that the plugin will have full context of what you want
              without you supplying it.
            </BodyText>
            <BodyText>
              Today, sprouts.design is best suited for app wireframes and prototypes, not for graphic design or logos.
            </BodyText>
            <div className="m-4 rounded border bg-slate-50 p-4">
              <h4 className="text-sm">Examples</h4>

              <BodyText>
                {' '}
                <strong className="text-sm font-semibold text-slate-700">✅ Good:</strong> the main screen for
                SuiteDreams, a new mobile app for booking luxury hotel rooms
              </BodyText>
              <BodyText>
                {' '}
                <strong className="text-sm font-semibold text-slate-700">✅ Good:</strong> pricing page for a saas
                website with 3 price points and a free trial. the page should feature a large call to action in the
                middle
              </BodyText>
              <BodyText>
                {' '}
                <strong className="text-sm font-semibold text-slate-700">❌ Bad:</strong> checkout
              </BodyText>
              <BodyText>
                {' '}
                <strong className="text-sm font-semibold text-slate-700">❌ Bad:</strong> SuiteDreams homepage
              </BodyText>
              <BodyText>
                {' '}
                <strong className="text-sm font-semibold text-slate-700">❌ Bad:</strong> owl logo
              </BodyText>
            </div>
          </AccordionItem>
          <AccordionItem header="How do I improve the components I'm getting?">
            <BodyText>
              Like prototypes, components will work best if you supply specific concepts such as a{' '}
              <em>"tweet card embed"</em> or <em>"order summary view"</em>.
            </BodyText>
            <BodyText>
              Refining components with Figma-speak usually delivers good results when targeted towards specific
              components:
            </BodyText>
            <div className="mb-4 mt-2">
              <Screenshot src="component-refinement.gif" />
            </div>
          </AccordionItem>
          <AccordionItem header="Why do I have to buy credits to keep using the plugin?">
            <BodyText>
              This plugin is backed by the latest AI deep learning models. While these are powerful, they're also not
              cheap! Purchasing credits are a way to help cover costs for running the plugin.
            </BodyText>
            <BodyText>
              Since this plugin is still new, I'm looking for feedback. If you have any suggestions, bug reports, or
              other feedback, I'll add credits to your account if you send them to me:{' '}
              <a className="text-green-800 underline hover:text-green-600" href="mailto:me@davidhu.me">
                me@davidhu.me
              </a>
              .
            </BodyText>
          </AccordionItem>
          <AccordionItem header="What affects how many credits are used per generation?">
            <BodyText>
              Credit usage is directly correlated with the costs of running the AI models. Most of the time, each run
              uses 2-3 credits and each refinement will cost 1-2 credits.
            </BodyText>
          </AccordionItem>
          <AccordionItem header="Who's behind this?">
            <BodyText>
              <a className="text-green-800 underline hover:text-green-600" href="https://davidhu.me">
                I am
              </a>
              ! I'd love your{' '}
              <Link href="/feedback" className="text-green-800 underline hover:text-green-600">
                direct feedback
              </Link>
              .
            </BodyText>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  )
}

export default Login
